import Navbar from "../components/Navbar";
import useAxios from "../utils/useAxios";
import { useState, useEffect, useRef, useMemo } from "react";
import { Checkbox, useToast } from "@chakra-ui/react";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  VStack,
  useColorModeValue,
  Heading,
  Skeleton,
  Link,
  Text,
  SimpleGrid,
  Button,
  Select,
  Flex,
  Icon,
  HStack,
  Tooltip,
  Divider,
} from "@chakra-ui/react";

import { Trans } from "@lingui/react/macro";

import DeviceWidget from "../components/DeviceComponents/DeviceWidget";
import { MdLocationOn, MdPower } from "react-icons/md";
import { GiValve } from "react-icons/gi";
import { FaMicrochip, FaSeedling, FaSun, FaBolt, FaTint, FaTachometerAlt } from "react-icons/fa";
import { FaServer, FaThLarge, FaUnlink } from "react-icons/fa";
import { FaBroom } from "react-icons/fa";
import { TiWeatherCloudy } from "react-icons/ti";
import { FaTemperatureHigh } from "react-icons/fa";
import { IField } from "../types/fieldInterface";
import { IDevice, IDevices } from "../types/deviceInterface";
import { useDeviceTileColor } from "../utils/colorUtils/deviceColorUtil";

const deviceTypeName = new Map<number, string>([
  [1, "Soil Sensors"],
  [3, "Environment Sensors"],
  [4, "Relays"],
  [6, "Valves"],
  [8, "Weather Stations"],
  [9, "Trackers"],
]);

const deviceTypeIcon = new Map<number, any>([
  [1, FaMicrochip],
  [3, FaTemperatureHigh],
  [4, MdPower],
  [6, GiValve],
  [8, TiWeatherCloudy],
  [9, MdLocationOn],
]);

const FIELD_ALL = 0;
const FIELD_UNASSIGNED = -1;
const FIELD_PLACEHOLDER = -2;

function deviceTypeLabel(typeId?: any): React.ReactNode {
  switch (typeId) {
    case 1:
      return <Trans>Soil Sensors</Trans>;
    case 3:
      return <Trans>Environment Sensors</Trans>;
    case 4:
      return <Trans>Relays</Trans>;
    case 6:
      return <Trans>Valves</Trans>;
    case 8:
      return <Trans>Weather Stations</Trans>;
    case 9:
      return <Trans>Trackers</Trans>;
    default:
      return <Trans>Unknown</Trans>;
  }
}

function deviceTypeFromString(name?: string): React.ReactNode {
  switch (name) {
    case "Soil Sensors":
      return <Trans>Soil Sensors</Trans>;
    case "Environment Sensors":
      return <Trans>Environment Sensors</Trans>;
    case "Relays":
      return <Trans>Relays</Trans>;
    case "Valves":
      return <Trans>Valves</Trans>;
    case "Weather Stations":
      return <Trans>Weather Stations</Trans>;
    case "Trackers":
      return <Trans>Trackers</Trans>;
    case "UNKNOWN":
      return <Trans>Unknown</Trans>;
    default:
      return <Trans>Unknown</Trans>;
  }
}

const ALL_SUMMARY_TYPES = [1, 3, 4, 6, 8, 9] as const;

const DeviceOverview = () => {
  const api = useAxios();
  const toast = useToast();

  const [devices, setDevices] = useState<IDevices>([]);
  const [fields, setFields] = useState<IField[]>([]);
  const [selectedField, setSelectedField] = useState<number>(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const [excludedTypes, setExcludedTypes] = useState<Set<number>>(new Set());

  const textColor = useColorModeValue("gray.800", "gray.200");
  const subtle = useColorModeValue("gray.600", "gray.400");
  const pillBg = useColorModeValue("blackAlpha.50", "whiteAlpha.100");

  const flag = useRef(false);

  const [isDevicesLoading, setIsDevicesLoading] = useState(true);
  const [isFieldsLoading, setIsFieldsLoading] = useState(true);
  const { noAlarmBgColor, devicesCountBgColor, notAssignedBgColor, deviceTypeBgColor } =
    useDeviceTileColor();

  useEffect(() => {
    if (flag.current) return;
    flag.current = true;

    const fetchDevices = async () => {
      try {
        const { data: devicesData } = await api.get("/frontend/devices/current-data/");
        setDevices(devicesData);
      } catch (error: any) {
        if (error.response?.status === 401) return;
        toast({
          title: "Error fetching devices data",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsDevicesLoading(false);
      }
    };
    fetchDevices();
  }, []);

  useEffect(() => {
    const fetchFields = async () => {
      try {
        const { data: fieldsData } = await api.get("/agtech/fields/");
        setFields(fieldsData);
        console.log("fileds: ", fieldsData);
      } catch (error: any) {
        if (error.response?.status === 401) return;
        toast({
          title: "Error fetching fields data",
          status: "error",
          duration: 9000,
          isClosable: true,
        });
      } finally {
        setIsFieldsLoading(false);
      }
    };
    fetchFields();
  }, []);

  const fieldFiltered = useMemo(() => {
    return devices.filter((dev) => {
      if (selectedField === FIELD_PLACEHOLDER) return false;
      if (selectedField === FIELD_ALL) return true;
      if (selectedField === FIELD_UNASSIGNED) return dev.field_id === null;
      return dev.field_id === selectedField;
    });
  }, [devices, selectedField]);

  const typeCounts = useMemo(() => {
    const counts = new Map<number, number>();
    for (const t of ALL_SUMMARY_TYPES) counts.set(t, 0);
    for (const d of fieldFiltered) {
      if (counts.has(d.device_type))
        counts.set(d.device_type, (counts.get(d.device_type) || 0) + 1);
    }
    return counts;
  }, [fieldFiltered]);

  const filteredDevices = useMemo(
    () => fieldFiltered.filter((d) => !excludedTypes.has(d.device_type)),
    [fieldFiltered, excludedTypes],
  );

  const groupedDevices = useMemo(() => {
    return filteredDevices.reduce(
      (groups: Record<string, IDevice[]>, device) => {
        const name = deviceTypeName.get(device.device_type) || "UNKNOWN";
        if (!groups[name]) groups[name] = [];
        groups[name].push(device);
        return groups;
      },
      {} as Record<string, IDevice[]>,
    );
  }, [filteredDevices]);

  const toggleType = (typeId: number) => {
    setExcludedTypes((prev) => {
      const nextSet = new Set(prev);
      if (nextSet.has(typeId)) nextSet.delete(typeId);
      else nextSet.add(typeId);

      const included = ALL_SUMMARY_TYPES.filter((t) => !nextSet.has(t));
      const qp = new URLSearchParams(searchParams);

      if (included.length === ALL_SUMMARY_TYPES.length) {
        qp.delete("device_type");
        qp.delete("hide");
      } else if (included.length === 1) {
        qp.set("device_type", String(included[0]));
        qp.delete("hide");
      } else {
        qp.set("hide", Array.from(nextSet).join(","));
        qp.delete("device_type");
      }

      setSearchParams(qp, { replace: true });
      return nextSet;
    });
  };

  const clearTypeFilters = () => {
    setExcludedTypes(new Set());
    const qp = new URLSearchParams(searchParams);
    qp.delete("hide");
    qp.delete("device_type");
    setSearchParams(qp, { replace: true });
  };

  useEffect(() => {
    const field = searchParams.get("field");
    const nextField = field ? Number(field) : 0;
    if (nextField !== selectedField) {
      setSelectedField(nextField);
    }

    const hide = searchParams.get("hide");
    const type = searchParams.get("device_type");
    let nextSet: Set<number>;
    if (hide) {
      nextSet = new Set(hide.split(",").map(Number));
    } else if (type && !Number.isNaN(Number(type))) {
      const one = Number(type);
      nextSet = new Set(ALL_SUMMARY_TYPES.filter((t) => t !== one));
    } else {
      nextSet = new Set();
    }

    const sameSize = nextSet.size === excludedTypes.size;
    const sameContent = sameSize && Array.from(nextSet).every((v) => excludedTypes.has(v));
    if (!sameContent) {
      setExcludedTypes(nextSet);
    }
  }, [searchParams]);

  return (
    <Navbar>
      <Box mx={{ base: 2, sm: 4, md: 6 }} p={5}>
        <VStack mt={2} spacing={4} align="stretch">
          <SimpleGrid minChildWidth={{ base: "40" }} spacing="5">
            <Heading fontSize={{ base: "2xl", sm: "3xl" }}>
              <Trans>Device overview</Trans>
            </Heading>
          </SimpleGrid>

          <Flex direction={{ base: "column", md: "row" }} gap={6} mb={2} align={{ md: "center" }}>
            <Box>
              <Text mb={2} fontWeight="semibold">
                <Trans>Filter by field</Trans>
              </Text>
              <Select
                value={selectedField}
                onChange={(e) => {
                  const v = Number(e.target.value);
                  setSelectedField(v);
                  const next = new URLSearchParams(searchParams);
                  if (v === FIELD_ALL) next.delete("field");
                  else next.set("field", String(v));
                  setSearchParams(next, { replace: true });
                }}
                maxW="300px"
              >
                <option value={FIELD_PLACEHOLDER}>
                  <Trans>Select field...</Trans>
                </option>
                <option value={FIELD_ALL}>
                  <Trans>All fields</Trans>
                </option>
                <option value={FIELD_UNASSIGNED}>
                  <Trans>Unattached (no field)</Trans>
                </option>
                {fields.map((field) => (
                  <option key={field.id} value={field.id}>
                    {field.field_name}
                  </option>
                ))}
              </Select>
            </Box>
          </Flex>

          <Box mt={4}>
            <VStack spacing={2} align="stretch" mb={2}>
              <Text fontWeight="bold">
                <Trans>Summary</Trans>
              </Text>
              <Text color="gray.500" fontSize={18}>
                <Trans>
                  Here is a quick overview of your devices connected to the field and not assigned
                </Trans>
              </Text>
            </VStack>

            <Divider opacity={0} />

            <Skeleton isLoaded={!isDevicesLoading} w="full" mt={2}>
              <SimpleGrid
                templateColumns={{ base: "repeat(2, max-content)", md: "repeat(3, max-content)" }}
                columnGap={4}
                rowGap={2}
                justifyContent="center"
                w="full"
                mt={2}
              >
                {[
                  {
                    icon: FaThLarge,
                    label: (
                      <>
                        {fields.length}{" "}
                        {fields.length === 1 ? <Trans>Field</Trans> : <Trans>Fields</Trans>}
                      </>
                    ),
                    bg: noAlarmBgColor,
                  },
                  {
                    icon: FaServer,
                    label: (
                      <>
                        {devices.length}{" "}
                        {devices.length === 1 ? <Trans>Device</Trans> : <Trans>Devices</Trans>}
                      </>
                    ),
                    bg: devicesCountBgColor,
                  },
                  {
                    icon: FaUnlink,
                    label: (
                      <>
                        {devices.filter((d) => d.field_id === null).length}{" "}
                        <Trans>Not assigned</Trans>
                      </>
                    ),
                    bg: notAssignedBgColor,
                  },
                ].map(({ icon, label, bg }, i) => (
                  <Flex
                    key={i}
                    bg={bg}
                    p={3}
                    borderRadius="md"
                    align="center"
                    justify="center"
                    minW="160px"
                  >
                    <Icon as={icon} boxSize={4} flexShrink={0} />
                    <Text fontWeight="semibold" ml={2} noOfLines={1} fontSize="sm">
                      {label}
                    </Text>
                  </Flex>
                ))}
              </SimpleGrid>
            </Skeleton>
          </Box>

          <Divider my={2} />

          <Box mt={6}>
            <Flex justify="space-between" align="center" mb={5}>
              <Text fontWeight="bold">
                <Trans>Filter by device type</Trans>
              </Text>
              <Button
                onClick={clearTypeFilters}
                size="sm"
                variant="outline"
                leftIcon={<FaBroom />}
                disabled={excludedTypes.size === 0}
                _disabled={{
                  opacity: 0.5,
                  cursor: "not-allowed",
                }}
                ml={4}
              >
                <Trans>Clear filters</Trans>
              </Button>
            </Flex>
            <Skeleton isLoaded={!isDevicesLoading} w="full">
              <SimpleGrid
                templateColumns="repeat(auto-fit, minmax(180px, 1fr))"
                gap={4}
                alignItems="stretch"
                w="full"
              >
                {ALL_SUMMARY_TYPES.map((typeId) => {
                  const IconCmp = deviceTypeIcon.get(typeId);
                  const count = typeCounts.get(typeId) || 0;
                  const isExcluded = excludedTypes.has(typeId);
                  const isChecked = !excludedTypes.has(typeId);

                  return (
                    <Tooltip key={typeId} hasArrow>
                      <Button
                        onClick={() => toggleType(typeId)}
                        variant={isChecked ? "solid" : "outline"}
                        borderRadius="lg"
                        w="full"
                        p={0}
                        overflow="hidden"
                        transition="all 0.12s ease"
                        _hover={{ boxShadow: "sm", transform: "translateY(-1px)" }}
                      >
                        <Flex
                          w="full"
                          align="center"
                          justify="center"
                          bg={isChecked ? deviceTypeBgColor || pillBg : "transparent"}
                          borderWidth={isChecked ? "0" : "1px"}
                          borderColor="gray.300"
                          p={3}
                          borderRadius="lg"
                        >
                          <HStack spacing={2} w="full" justify="center" minW={0}>
                            {IconCmp && (
                              <Icon
                                as={IconCmp}
                                boxSize={4}
                                flexShrink={0}
                                color={isChecked ? "white" : textColor}
                              />
                            )}
                            <Text
                              fontWeight="semibold"
                              noOfLines={1}
                              fontSize="sm"
                              color={isChecked ? "white" : textColor}
                              minW={0}
                            >
                              {deviceTypeLabel(typeId)}
                            </Text>
                            <Box
                              as="span"
                              fontWeight="semibold"
                              aria-label="count"
                              color={isChecked ? "white" : textColor}
                              p={1}
                              flexShrink={0}
                            >
                              {count}
                            </Box>
                          </HStack>
                          <Checkbox
                            isChecked={isChecked}
                            pointerEvents="none"
                            mr={2}
                            variant="outline"
                            colorScheme="blue.500"
                          />
                        </Flex>
                      </Button>
                    </Tooltip>
                  );
                })}
              </SimpleGrid>
            </Skeleton>
          </Box>

          <Divider my={2} />

          {isDevicesLoading ? (
            <Skeleton height="20px" />
          ) : Object.entries(groupedDevices).length === 0 ? (
            <Text color={subtle} py={6}>
              <Trans>No devices match the current filters.</Trans>
            </Text>
          ) : (
            Object.entries(groupedDevices)
              .sort(([keyA], [keyB]) => {
                if (keyA === "UNKNOWN") return 1;
                if (keyB === "UNKNOWN") return -1;
                return keyA.localeCompare(keyB);
              })
              .map(([typeName, devs]) => (
                <Box key={typeName} mt={4}>
                  <Heading size="md" mb={2}>
                    {deviceTypeFromString(typeName)}
                  </Heading>
                  <SimpleGrid
                    minChildWidth={{ base: "100%", sm: "260px", md: "300px" }}
                    spacing={4}
                  >
                    {devs.map((device) => (
                      <Link
                        key={device.device_id}
                        href={
                          device.device_type === 8
                            ? `/weather-station/${device.device_id}`
                            : device.device_type === 6
                              ? `/valve-view/${device.device_id}`
                              : device.device_type === 9
                                ? `/tracker/${device.device_id}`
                                : `/devices/${device.device_id}`
                        }
                        style={{ display: "block", width: "100%" }}
                      >
                        <DeviceWidget device={device} />
                      </Link>
                    ))}
                  </SimpleGrid>
                </Box>
              ))
          )}
        </VStack>
      </Box>
    </Navbar>
  );
};

export default DeviceOverview;
